import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Calendar, Clock } from "lucide-react";
import { toast } from "sonner";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { useLang } from "@/lib/i18n";

const TABS = ["one_way", "round_trip", "hourly"];

export default function BookingBar() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [type, setType] = useState("one_way");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [time, setTime] = useState("09:00");

  const go = () => {
    const okFrom = from.trim().length > 2;
    const okTo = type === "hourly" || to.trim().length > 2;
    if (!okFrom || !okTo) {
      toast.error(t("bar.missing"), { description: t("bar.missing_desc") });
      return;
    }
    navigate(`/reservation?type=${type}&from=${encodeURIComponent(from.trim())}&to=${encodeURIComponent(to.trim())}&date=${date}&time=${time}`);
  };

  const selectCls = "on-dark w-full bg-transparent text-sm font-medium text-white outline-none";

  return (
    <div
      id="booking-bar"
      data-testid="booking-bar"
      className="relative z-40 rounded-2xl border border-white/15 bg-miles-deep/60 shadow-[0_40px_100px_-20px_rgba(7,12,26,0.6)] backdrop-blur-2xl"
    >
      <div className="flex items-center justify-between gap-2 px-3 pt-3 sm:px-4">
        <div className="flex gap-1" data-testid="booking-bar-tabs">
          {TABS.map((id) => (
            <button key={id} onClick={() => setType(id)} data-testid={`booking-bar-tab-${id}`}
              className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-all duration-300 ${
                type === id ? "bg-white text-miles-deep" : "text-white/65 hover:text-white"
              }`}>
              {t(`bar.${id}`)}
            </button>
          ))}
        </div>
        <span className="hidden items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-white/50 sm:flex">
          <span className="relative flex h-1.5 w-1.5">
            <span className="ping-soft absolute inline-flex h-full w-full rounded-full bg-miles-electric" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-miles-electric" />
          </span>
          {t("bar.available")}
        </span>
      </div>

      <div className="mt-2 grid lg:grid-cols-[1.25fr_1.25fr_0.85fr_0.7fr_auto]">
        <div className="group flex items-center gap-3 border-t border-white/10 px-4 py-4 transition-colors hover:bg-white/5 lg:border-l-0 lg:border-t-0">
          <span className="relative flex h-2 w-2 shrink-0">
            <span className="absolute inline-flex h-full w-full rounded-full bg-miles-electric" />
          </span>
          <AddressAutocomplete
            dark
            label={t("bar.from")}
            value={from}
            onChange={setFrom}
            placeholder={t("bar.from_ph")}
            testId="booking-bar-pickup"
          />
        </div>

        {type !== "hourly" ? (
          <div className="flex items-center gap-3 border-t border-white/10 px-4 py-4 transition-colors hover:bg-white/5 lg:border-l lg:border-t-0">
            <span className="h-2 w-2 shrink-0 rounded-full border-2 border-white/70" />
            <AddressAutocomplete
              dark
              label={t("bar.to")}
              value={to}
              onChange={setTo}
              placeholder={t("bar.to_ph")}
              testId="booking-bar-dropoff"
            />
          </div>
        ) : (
          <div className="hidden items-center border-l border-white/10 px-4 lg:flex">
            <p className="text-xs leading-relaxed text-white/55">{t("bar.hourly_note")}</p>
          </div>
        )}

        <label className="flex items-center gap-3 border-t border-white/10 px-4 py-4 transition-colors hover:bg-white/5 lg:border-l lg:border-t-0">
          <Calendar className="h-4 w-4 shrink-0 text-white/55" strokeWidth={1.5} />
          <span className="w-full">
            <span className="block text-[10px] font-semibold uppercase tracking-[0.2em] text-white/55">{t("bar.date")}</span>
            <input type="date" value={date} min={new Date().toISOString().slice(0, 10)} onChange={(e) => setDate(e.target.value)}
              data-testid="booking-bar-date" className={`${selectCls} [color-scheme:dark]`} />
          </span>
        </label>

        <label className="flex items-center gap-3 border-t border-white/10 px-4 py-4 transition-colors hover:bg-white/5 lg:border-l lg:border-t-0">
          <Clock className="h-4 w-4 shrink-0 text-white/55" strokeWidth={1.5} />
          <span className="w-full">
            <span className="block text-[10px] font-semibold uppercase tracking-[0.2em] text-white/55">{t("bar.time")}</span>
            <input type="time" value={time} onChange={(e) => setTime(e.target.value)}
              data-testid="booking-bar-time" className={`${selectCls} [color-scheme:dark]`} />
          </span>
        </label>

        <div className="border-t border-white/10 p-2 lg:border-l lg:border-t-0">
          <motion.button
            onClick={go}
            whileTap={{ scale: 0.97 }}
            data-testid="booking-bar-submit"
            className="flex h-full w-full items-center justify-center gap-2 rounded-xl bg-miles px-7 py-4 text-sm font-semibold text-white transition-colors duration-300 hover:bg-miles-dark lg:py-0"
          >
            {t("bar.submit")} <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
          </motion.button>
        </div>
      </div>

      {type !== "hourly" && (
        <motion.div
          key={`${from}-${to}`}
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="h-px origin-left bg-gradient-to-r from-miles via-miles-electric to-miles/40"
          data-testid="booking-route-line"
        />
      )}

      <div className="flex items-center justify-between gap-3 border-t border-white/10 px-4 py-3">
        <Link to="/suivi" data-testid="booking-live-ticker"
          className="group flex min-w-0 items-center gap-2.5">
          <span className="relative flex h-1.5 w-1.5 shrink-0">
            <span className="ping-soft absolute inline-flex h-full w-full rounded-full bg-emerald-400" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
          </span>
          <span className="truncate text-xs text-white/70 transition-colors group-hover:text-white">
            {t("bar.ticker")}
          </span>
          <ArrowUpRight className="h-3.5 w-3.5 shrink-0 text-white/40 transition-colors group-hover:text-miles-electric" strokeWidth={1.5} />
        </Link>
        <span className="hidden shrink-0 font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 sm:block">Genève → GVA</span>
      </div>
    </div>
  );
}
